import { faSquare, faSquareCheck } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _, { last } from 'lodash';
import * as React from 'react';
import { SelectHTMLAttributes, useState } from 'react';
import { useOutsideClickHandler } from '../../../logic/otherUtilities/useOutsideClickHandler';
import VoxelIconButton from '../../voxel/Buttons/VoxelIconButton';
import { GenericSelectOption } from '../../voxel/Forms/VoxelSelect';
import VoxelText from '../../voxel/Text/VoxelText';

export interface AverageCTRMultiSelectProps
  extends Pick<SelectHTMLAttributes<HTMLSelectElement>, 'autoFocus' | 'disabled'> {
  id?: string;
  label?: string;
  labelPosition?: 'top' | 'left';
  outlined?: boolean;
  selectedOptions?: GenericSelectOption[];
  setSelectedOptions: (option: GenericSelectOption[]) => void;
  options: GenericSelectOption[];
  placeholder?: string;
  helperText?: string;
  showHelperText?: boolean;
  size?: 'sm' | 'md';
  width?: 'w-16' | 'w-20' | 'w-24' | 'w-28' | 'w-fit' | 'w-full';
}

const groupSequences = (array: number[]) => {
  return _.reduce(
    array,
    function (result, value, index, collection) {
      if (value - collection[index - 1] === 1) {
        const group = last(result);
        group.push(value);
      } else {
        result.push([value]);
      }
      return result;
    },
    []
  );
};

export const selectedRanks = (array: number[]) => {
  let sequences = groupSequences(array.sort((a, b) => a - b));
  let stringList = sequences.map((x: number[]) => {
    if (x.length > 1) {
      return Math.min(...x).toString() + '-' + Math.max(...x).toString();
    } else return x.toString();
  });
  return 'Rank ' + stringList.join(', ');
};

const AverageCTRMultiSelect: React.FC<AverageCTRMultiSelectProps> = ({
  label,
  labelPosition = 'left',
  id,
  disabled,
  selectedOptions,
  setSelectedOptions,
  outlined = true,
  placeholder,
  options,
  helperText,
  showHelperText,
  size = 'md',
  width = 'w-full',
}) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);

  // Click outside form field
  const divRef = useOutsideClickHandler<HTMLDivElement>({
    action: () => setShowOptions(false),
    active: showOptions,
  });

  const handleSelectOption = (item: GenericSelectOption) => {
    let isInCurrent = selectedOptions.some((t) => t.title === item.title);
    if (isInCurrent) {
      let newArray = selectedOptions.filter((v) => v.title !== item.title);
      setSelectedOptions(newArray);
    } else {
      let test = [...selectedOptions, item];
      setSelectedOptions(test);
    }
  };

  let labelPositionCSS = '';
  if (labelPosition === 'left') {
    labelPositionCSS = 'flex-row flex-nowrap items-center gap-x-2';
  }
  if (labelPosition === 'top') {
    labelPositionCSS = 'flex-col gap-y-2';
  }
  return (
    <div className={`flex ${labelPositionCSS}`}>
      <label htmlFor={id}>
        <VoxelText variant="form-caption">{label}</VoxelText>
      </label>
      <div className="relative group" ref={divRef}>
        <input
          id={id}
          readOnly
          onClick={() => setShowOptions((prev) => !prev)}
          placeholder=""
          value={selectedRanks(selectedOptions?.map((t) => t.value))}
          className={`
          ${width} px-2.5
          ${size === 'md' ? 'py-2' : 'py-1'}
            rounded-md
            outline-none
            select-none
            ${!disabled && 'cursor-pointer'}
            ${outlined && !showHelperText && 'ring-voxel-gray-300 ring-1'}
            ${showHelperText && 'ring-red-500 ring-2'} 
            ${!outlined && !showOptions && 'bg-transparent'}
            hover:ring-voxel-gray-400 hover:ring-1
            focus:ring-voxel-gray-600 focus:ring-2
            ${showOptions && 'ring-2 ring-voxel-gray-600'}
            transition duration-100
            appearance-none
          `}
        />
        {!disabled && (
          <div className={`absolute z-50 right-1 ${size === 'md' ? 'top-1.5 ' : 'top-0.5'}`}>
            <VoxelIconButton
              size="xs"
              color="white"
              icon={<FontAwesomeIcon icon={showOptions ? faAngleUp : faAngleDown} />}
              onClick={() => setShowOptions((prev) => !prev)}
            />
          </div>
        )}
        {showOptions && (
          <ul
            className={`
              left-0 right-0 top-10 absolute z-[1200] bg-white rounded-md shadow-lg ring-1 ring-gray-100 cursor-pointer w-fit min-w-full 
              max-h-[520px] 
              overflow-y-auto scroll
              select-none
            `}
          >
            {options.map((item, index) => (
              <li
                key={item.value}
                onClick={() => handleSelectOption(item)}
                className="w-full py-2 px-3 hover:bg-voxel-gray-100 flex flex-row items-center gap-4"
              >
                <FontAwesomeIcon
                  icon={
                    selectedOptions.some((t) => t.title === item.title) ? faSquareCheck : faSquare
                  }
                  size="lg"
                />
                <VoxelText variant="bread-medium">{item?.title}</VoxelText>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AverageCTRMultiSelect;
